import { allGeoAreasSelector } from '@/redux/modules/geoArea/geoArea.selectors';
import { AuctionsNearMeQueryParams } from '../types/AuctionsNearMe.types';
import { CatalogNearMe } from '@/redux/modules/auctionsNearMe/catalogsNearMe.types';
import { createBoundingBoxString } from '../helpers/createBoundingBoxString';
import {
    defaultAuctionsNearMeUkBounds,
    defaultAuctionsNearMeUsaBounds,
    defaultPageNumber,
    MAX_CATALOGS_TO_FETCH,
} from '../constants/AuctionsNearMeConstants';
import { fetchCatalogsNearMeByBounds } from '@/redux/modules/auctionsNearMe/auctionsNearMe.actions';
import { getCatalogsNearMeSelector } from '@/redux/modules/auctionsNearMe/auctionsNearMe.selectors';
import { getLocale } from '@/redux/modules/intl';
import { MapBounds } from '../components/MapView/components/GoogleMap/AuctionsNearMeGoogleMap';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useGeoAreaFromUrl } from './useGeoAreaFromUrl';
import { useGeocodeLocation } from './useGeocodeLocation';
import { useGetQueryParams } from '@/hooks/queryParams/useGetQueryParams';

export const useAuctionsNearMeData = () => {
    const dispatch = useAppDispatch();
    const getQueryParams = useGetQueryParams<AuctionsNearMeQueryParams>();
    const queryParams = getQueryParams();

    // Extracted hooks
    const { geoArea, hasNewUrlParams, hasOldUrlParams, routeParams } = useGeoAreaFromUrl();

    // Redux selectors
    const allGeoAreas = useAppSelector(allGeoAreasSelector);
    const catalogsNearMe = useAppSelector(getCatalogsNearMeSelector);
    const locale = useAppSelector(getLocale);

    // Track if geoAreas have been loaded (prevents geocoding before we know if geoArea exists)
    const geoAreasLoaded = allGeoAreas.length > 0;

    // Local state
    const [googleMapsReady, setGoogleMapsReady] = useState(false);
    const [customSearchBounds, setCustomSearchBounds] = useState<MapBounds | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [geocoderFailed, setGeocoderFailed] = useState(false);

    // Refs
    const lastFetchParamsRef = useRef<{ bounds: string } | null>(null);
    const currentActiveBoundsRef = useRef<MapBounds | null>(null);

    // Derived values
    const catalogIds = useMemo(() => catalogsNearMe.map((catalog: CatalogNearMe) => catalog.id), [catalogsNearMe]);
    const hasFilteredResults = catalogsNearMe.length > 0;

    const defaultBounds = useMemo(
        () => (locale === 'en-gb' ? defaultAuctionsNearMeUkBounds : defaultAuctionsNearMeUsaBounds),
        [locale]
    );

    // Determine location to geocode
    // Only geocode URL params if geoAreas are loaded but no matching geoArea found (fallback for unknown locations)
    const locationToGeocode = useMemo(() => {
        if (queryParams.search && !customSearchBounds) {
            return queryParams.search;
        }

        // For URL params, only geocode if geoAreas are loaded but no match found
        if (hasNewUrlParams && !geoArea && !customSearchBounds && geoAreasLoaded) {
            if ('city' in routeParams && 'state' in routeParams && routeParams.city && routeParams.state) {
                return `${routeParams.city}, ${routeParams.state}`;
            }
        }

        return null;
    }, [queryParams.search, customSearchBounds, hasNewUrlParams, geoArea, routeParams, geoAreasLoaded]);

    // Geocode location (consolidates both geocoding effects)
    const {
        bounds: geocodedBounds,
        failed: geocodingFailed,
        isGeocoding,
    } = useGeocodeLocation({
        googleMapsReady,
        location: locationToGeocode,
    });

    // Sync geocoding failed state from hook
    useEffect(() => {
        if (geocodingFailed) {
            setGeocoderFailed(true);
        }
    }, [geocodingFailed]);

    // Sync geocoded bounds and search term when geocoding completes
    useEffect(() => {
        if (!geocodedBounds || !locationToGeocode) {
            return;
        }

        setSearchTerm(locationToGeocode);
        setCustomSearchBounds(geocodedBounds);
        currentActiveBoundsRef.current = geocodedBounds;

        const insideBoundingBox = createBoundingBoxString(geocodedBounds);
        const shouldFetch = !lastFetchParamsRef.current || lastFetchParamsRef.current.bounds !== insideBoundingBox;

        if (!shouldFetch) {
            return;
        }

        lastFetchParamsRef.current = { bounds: insideBoundingBox };

        dispatch(
            fetchCatalogsNearMeByBounds({
                insideBoundingBox,
                page: defaultPageNumber,
                pageSize: MAX_CATALOGS_TO_FETCH,
            })
        );
    }, [geocodedBounds, locationToGeocode, dispatch]);

    // Sync geocoder failed state and search term
    useEffect(() => {
        if (geocoderFailed && locationToGeocode) {
            setSearchTerm(locationToGeocode);
        }
    }, [geocoderFailed, locationToGeocode]);

    // Calculate map bounds in priority order:
    // - If we have a geoArea from URL params, always use it (ignore stale customSearchBounds)
    // - Otherwise: customSearchBounds > default bounds
    const mapBounds = useMemo(() => {
        // When on a geoArea route, always use geoArea bounds (handles navigation between geoAreas)
        if (geoArea?.northEastLatitude && geoArea?.southWestLatitude) {
            return {
                northEast: { lat: geoArea.northEastLatitude, lng: geoArea.northEastLongitude },
                southWest: { lat: geoArea.southWestLatitude, lng: geoArea.southWestLongitude },
            };
        }

        // For custom searches or base page
        if (customSearchBounds) {
            return customSearchBounds;
        }

        return defaultBounds;
    }, [customSearchBounds, geoArea, defaultBounds]);

    // Handlers
    const handleSearchResults = useCallback(
        ({ northEast, southWest }: MapBounds) => {
            const bounds = { northEast, southWest };
            const insideBoundingBox = createBoundingBoxString(bounds);
            const shouldFetch = !lastFetchParamsRef.current || lastFetchParamsRef.current.bounds !== insideBoundingBox;

            currentActiveBoundsRef.current = bounds;
            setCustomSearchBounds(bounds);

            if (!shouldFetch) {
                return;
            }

            lastFetchParamsRef.current = { bounds: insideBoundingBox };

            dispatch(
                fetchCatalogsNearMeByBounds({
                    insideBoundingBox,
                    page: defaultPageNumber,
                    pageSize: MAX_CATALOGS_TO_FETCH,
                })
            );
        },
        [dispatch]
    );

    const handleBoundsChange = useCallback(
        ({ northEast, southWest }: MapBounds) => {
            // Skip if we're waiting for geocoding to complete - the geocoding effect will handle the fetch
            if (locationToGeocode && !customSearchBounds) {
                return;
            }

            const bounds = { northEast, southWest };
            const insideBoundingBox = createBoundingBoxString(bounds);

            currentActiveBoundsRef.current = bounds;

            // Only fetch if bounds actually changed
            const shouldFetch = !lastFetchParamsRef.current || lastFetchParamsRef.current.bounds !== insideBoundingBox;
            if (!shouldFetch) {
                return;
            }

            lastFetchParamsRef.current = { bounds: insideBoundingBox };

            dispatch(
                fetchCatalogsNearMeByBounds({
                    insideBoundingBox,
                    page: defaultPageNumber,
                    pageSize: MAX_CATALOGS_TO_FETCH,
                })
            );
        },
        [dispatch, locationToGeocode, customSearchBounds]
    );

    const handleGoogleMapsReady = useCallback(() => {
        setGoogleMapsReady(true);
    }, []);

    // Build a stable key from route params to detect navigation between different URLs
    const routeKey = useMemo(() => {
        if ('geoAreaId' in routeParams && routeParams.geoAreaId) {
            return `old:${routeParams.geoAreaId}`;
        }
        if ('city' in routeParams || 'state' in routeParams || 'country' in routeParams) {
            const city = 'city' in routeParams ? routeParams.city : '';
            const state = 'state' in routeParams ? routeParams.state : '';
            const country = 'country' in routeParams ? routeParams.country : '';
            return `new:${city}:${state}:${country}`;
        }
        return '';
    }, [routeParams]);

    // Effect: Clear custom search bounds when navigating to a predefined geo area
    // Must track actual route changes, not just hasNewUrlParams (which stays true when navigating between geoAreas)
    useEffect(() => {
        if (routeKey) {
            setCustomSearchBounds(null);
            setSearchTerm('');
            currentActiveBoundsRef.current = null;
            lastFetchParamsRef.current = null;
        }
    }, [routeKey]);

    // Effect: Reset to defaults when navigating to base page
    useEffect(() => {
        // Only run on base route: no URL params, no search query, no geoArea
        if (hasNewUrlParams || hasOldUrlParams || queryParams.search || geoArea) {
            return;
        }

        // If we have customSearchBounds set from a previous page, clear it and fetch defaults
        if (customSearchBounds) {
            setCustomSearchBounds(null);
            setSearchTerm('');
            currentActiveBoundsRef.current = null;
            lastFetchParamsRef.current = null;

            // Fetch default bounds
            const insideBoundingBox = createBoundingBoxString(defaultBounds);
            lastFetchParamsRef.current = { bounds: insideBoundingBox };

            dispatch(
                fetchCatalogsNearMeByBounds({
                    insideBoundingBox,
                    page: defaultPageNumber,
                    pageSize: MAX_CATALOGS_TO_FETCH,
                })
            );
        }
    }, [hasNewUrlParams, hasOldUrlParams, queryParams.search, geoArea, customSearchBounds, defaultBounds, dispatch]);

    // Effect: Fetch catalogs for predefined geoArea (from RelatedGeoAreas links)
    useEffect(() => {
        if (!geoArea || customSearchBounds || queryParams.search) {
            return;
        }

        if (!geoArea.northEastLatitude || !geoArea.southWestLatitude) {
            return;
        }

        const bounds: MapBounds = {
            northEast: { lat: geoArea.northEastLatitude, lng: geoArea.northEastLongitude },
            southWest: { lat: geoArea.southWestLatitude, lng: geoArea.southWestLongitude },
        };

        const insideBoundingBox = createBoundingBoxString(bounds);

        // Only fetch if bounds actually changed
        const paramsChanged = !lastFetchParamsRef.current || lastFetchParamsRef.current.bounds !== insideBoundingBox;

        if (!paramsChanged) {
            return;
        }

        currentActiveBoundsRef.current = bounds;
        lastFetchParamsRef.current = { bounds: insideBoundingBox };

        dispatch(
            fetchCatalogsNearMeByBounds({
                insideBoundingBox,
                page: defaultPageNumber,
                pageSize: MAX_CATALOGS_TO_FETCH,
            })
        );
    }, [geoArea, customSearchBounds, queryParams.search, dispatch]);

    return {
        catalogIds,
        catalogsNearMe,
        geoArea,
        geocoderFailed,
        handleBoundsChange,
        handleGoogleMapsReady,
        handleSearchResults,
        hasFilteredResults,
        isGeocoding,
        mapBounds,
        searchTerm,
        setGeocoderFailed,
        setSearchTerm,
    };
};
